import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Row, Label, Input, Button, Badge } from "@/Components/ui";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import Select from "react-select";
import moment from "moment";
import { GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { buildGroupedPricePayload, getGroupedPriceState, type GroupedPriceState } from "./pricePolicy";

interface PeriodItemProps {
    period: any;
    packagePrice: any;
    onPeriodUpdate: (data: any) => void;
    onPeriodDelete: (id: number) => void;
    onPriceCreate: (periodId: number) => void;
    onPriceUpdate: (ppId: string, data: any) => void;
    onPriceDelete: (ppId: string) => void;
    dragHandleProps?: any; // Props for the drag handle
    isPeriodSaving?: boolean;
    isPriceSaving?: boolean;
}

const periodTypes = [
    { value: "성수기", label: "성수기" },
    { value: "비수기", label: "비수기" },
    { value: "극성수기", label: "극성수기" },
    { value: "준성수기", label: "준성수기" },
    { value: "할인기간", label: "할인기간" },
    { value: "지정기간", label: "지정기간" },
    { value: "휴무", label: "휴무" }
];

const PeriodItem: React.FC<PeriodItemProps> = ({
    period,
    packagePrice,
    onPeriodUpdate,
    onPeriodDelete,
    onPriceCreate,
    onPriceUpdate,
    onPriceDelete,
    dragHandleProps,
    isPeriodSaving = false,
    isPriceSaving = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    // Local state for Period editing
    const [name, setName] = useState(period.name);
    const [periodType, setPeriodType] = useState(periodTypes.find(p => p.value === period.period_type));
    const [startDate, setStartDate] = useState(new Date(period.start_date));
    const [endDate, setEndDate] = useState(new Date(period.end_date));
    const [closeDays, setCloseDays] = useState({
        sun: period.close_day_sun,
        mon: period.close_day_mon,
        tue: period.close_day_tue,
        wed: period.close_day_wed,
        thu: period.close_day_thu,
        fri: period.close_day_fri,
        sat: period.close_day_sat,
        holiday: period.close_day_holiday,
    });

    // Local state for Price editing
    const [prices, setPrices] = useState<GroupedPriceState>({
        weekdayGroupPrice: 0,
        weekendGroupPrice: 0,
    });

    useEffect(() => {
        if (packagePrice) {
            setPrices(getGroupedPriceState(packagePrice));
        } else {
            setPrices({
                weekdayGroupPrice: 0,
                weekendGroupPrice: 0,
            });
        }
    }, [packagePrice]);

    useEffect(() => {
        // Sync local state when prop updates (e.g. after save)
        setName(period.name);
        setPeriodType(periodTypes.find(p => p.value === period.period_type));
        setStartDate(new Date(period.start_date));
        setEndDate(new Date(period.end_date));
        setCloseDays({
            sun: period.close_day_sun,
            mon: period.close_day_mon,
            tue: period.close_day_tue,
            wed: period.close_day_wed,
            thu: period.close_day_thu,
            fri: period.close_day_fri,
            sat: period.close_day_sat,
            holiday: period.close_day_holiday,
        });
    }, [period]);


    const handlePeriodSave = (e: React.MouseEvent) => {

        onPeriodUpdate({
            id: period.id,
            name,
            period_type: periodType?.value,
            start_date: moment(startDate).format("YYYY-MM-DD"),
            end_date: moment(endDate).format("YYYY-MM-DD"),
            priority: period.priority, // Keep existing priority
            close_day_sun: closeDays.sun,
            close_day_mon: closeDays.mon,
            close_day_tue: closeDays.tue,
            close_day_wed: closeDays.wed,
            close_day_thu: closeDays.thu,
            close_day_fri: closeDays.fri,
            close_day_sat: closeDays.sat,
            close_day_holiday: closeDays.holiday,
        });
    };

    const handlePriceSave = (e: React.MouseEvent) => {

        if (!packagePrice) return;
        onPriceUpdate(packagePrice.id, {
            period: period.id,
            ...buildGroupedPricePayload(prices),
        });
    };

    const toggleCloseDay = (day: keyof typeof closeDays) => {
        setCloseDays(prev => ({ ...prev, [day]: !prev[day] }));
    };

    const isActive = !!packagePrice;
    const groupedPrice = getGroupedPriceState(packagePrice);

    return (
        <Card className="mb-2 border shadow-none" style={{ background: isActive ? "#fff" : "#f8f9fa" }}>
            <CardBody className="p-2">
                <div className="d-flex align-items-center gap-3">
                    {/* Drag Handle */}
                    <div {...dragHandleProps} style={{ cursor: "grab" }} className={`text-muted ${dragHandleProps?.className || ""}`}>
                        <GripVertical size={20} />
                    </div>

                    {/* Main Info */}
                    <div className="flex-grow-1" onClick={() => setIsOpen(!isOpen)} style={{ cursor: "pointer" }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="fw-bold fs-15">{period.name}</span>
                            {isActive ? (
                                <Badge color="success">판매중</Badge>
                            ) : (
                                <Badge color="secondary">비활성화</Badge>
                            )}
                            <span className="text-muted fs-12 ms-2">
                                {period.start_date} ~ {period.end_date}
                            </span>
                        </div>
                        {packagePrice && (
                            <div className="text-muted fs-12">
                                평일·금요일: {groupedPrice.weekdayGroupPrice?.toLocaleString()} /
                                주말·휴일: {groupedPrice.weekendGroupPrice?.toLocaleString()}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="d-flex align-items-center gap-2">
                        {!isActive ? (
                            <Button
                                type="button"
                                size="sm"
                                color="primary"
                                outline
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPriceCreate(period.id);
                                }}
                                disabled={isPriceSaving}
                            >
                                {isPriceSaving ? '활성화 중...' : '활성화 (가격설정)'}
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                size="sm"
                                color="danger"
                                outline
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPriceDelete(packagePrice.id);
                                }}
                                disabled={isPriceSaving}
                            >
                                {isPriceSaving ? '비활성화 중...' : '비활성화'}
                            </Button>
                        )}
                        <Button type="button" color="link" className="p-0 text-dark" onClick={(e) => {

                            setIsOpen(!isOpen);
                        }}>
                            {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </Button>
                    </div>
                </div>

                {isOpen && (
                    <div className="mt-3 pt-3 border-top">
                        <Row>
                            {/* Left: Period Settings */}
                            <Col md={6} className="border-end">
                                <h6 className="mb-3 fw-bold">기간 정보 수정</h6>
                                <div className="mb-2">
                                    <Label className="form-label-sm">기간명</Label>
                                    <Input bsSize="sm" value={name} onChange={(e) => setName(e.target.value)} />
                                </div>
                                <Row className="mb-2">
                                    <Col xs={6}>
                                        <Label className="form-label-sm">시작일</Label>
                                        <CommonDatePicker
                                            className="form-control form-control-sm"
                                            value={startDate}
                                            onChange={(([date]) => setStartDate(date))}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <Label className="form-label-sm">종료일</Label>
                                        <CommonDatePicker
                                            className="form-control form-control-sm"
                                            value={endDate}
                                            onChange={(([date]) => setEndDate(date))}
                                        />
                                    </Col>
                                </Row>
                                <div className="mb-2">
                                    <Label className="form-label-sm">정기 휴무</Label>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {Object.keys(closeDays).map((day: any) => (
                                            <div key={day} className="d-flex align-items-center gap-1">
                                                <Input
                                                    type="checkbox"
                                                    id={`close-${period.id}-${day}`}
                                                    checked={!!closeDays[day]}
                                                    onChange={() => toggleCloseDay(day)}
                                                />
                                                <Label className="m-0 fs-12 cursor-pointer" htmlFor={`close-${period.id}-${day}`}>
                                                    {{
                                                        sun: "일", mon: "월", tue: "화", wed: "수", thu: "목", fri: "금", sat: "토", holiday: "공휴일"
                                                    }[day]}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="d-flex justify-content-between mt-3">
                                    <Button
                                        type="button"
                                        size="sm"
                                        color="danger"
                                        outline
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onPeriodDelete(period.id);
                                        }}
                                        disabled={isPeriodSaving}
                                    >
                                        기간 삭제
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        color="primary"
                                        onClick={handlePeriodSave}
                                        disabled={isPeriodSaving}
                                    >
                                        {isPeriodSaving ? '저장 중...' : '기간 정보 저장'}
                                    </Button>
                                </div>
                            </Col>

                            {/* Right: Price Settings (if active) */}
                            <Col md={6}>
                                <h6 className="mb-3 fw-bold">가격 설정 {isActive ? "" : "(비활성)"}</h6>
                                {isActive ? (
                                    <>
                                        <Row className="mb-2">
                                            <Col xs={6}>
                                                <Label className="form-label-sm">평일·금요일</Label>
                                                <Input type="number" bsSize="sm" value={prices.weekdayGroupPrice} onChange={(e) => setPrices({ ...prices, weekdayGroupPrice: parseInt(e.target.value) || 0 })} />
                                            </Col>
                                            <Col xs={6}>
                                                <Label className="form-label-sm">주말·휴일</Label>
                                                <Input type="number" bsSize="sm" value={prices.weekendGroupPrice} onChange={(e) => setPrices({ ...prices, weekendGroupPrice: parseInt(e.target.value) || 0 })} />
                                            </Col>
                                        </Row>
                                        <div className="text-end mt-3">
                                            <Button
                                                type="button"
                                                size="sm"
                                                color="success"
                                                onClick={handlePriceSave}
                                                disabled={isPriceSaving}
                                            >
                                                {isPriceSaving ? '저장 중...' : '가격 저장'}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-75 text-muted">
                                        <p>이 기간에 대한 상품 판매가 설정되지 않았습니다.</p>
                                        <Button
                                            type="button"
                                            size="sm"
                                            color="primary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onPriceCreate(period.id);
                                            }}
                                            disabled={isPriceSaving}
                                        >
                                            {isPriceSaving ? '설정 중...' : '판매 시작 (가격 설정)'}
                                        </Button>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default PeriodItem;
